import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';
import { hasAnyPermission } from '../../../utils/hrmsPermissions';
import { getSelectedBranchId } from '../../../utils/branchUtils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CommunicationContext = createContext(null);

export function useCommunication() {
  const ctx = useContext(CommunicationContext);
  if (!ctx) throw new Error('useCommunication must be used inside CommunicationProvider');
  return ctx;
}

export function CommunicationProvider({ children }) {
  const { user } = useAuth();

  // ── Shared state ──────────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState({});
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [batches, setBatches] = useState([]);
  const [twilioStatus, setTwilioStatus] = useState(null);
  const [waTemplates, setWaTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [waTemplateFetchError, setWaTemplateFetchError] = useState(false);
  const [smsTemplateFetchError, setSmsTemplateFetchError] = useState(false);
  const [notifRules, setNotifRules] = useState({});
  const [notifEventRegistry, setNotifEventRegistry] = useState([]);
  const [localTemplates, setLocalTemplates] = useState([]);
  const [localTemplatesLoading, setLocalTemplatesLoading] = useState(false);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, danger: true });
  // onCancel runs when the dialog closes without confirming (Cancel, Escape, backdrop).
  const confirmCancelRef = useRef(null);
  const openConfirmDialog = ({ title, message, onConfirm, onCancel, danger = true }) => {
    confirmCancelRef.current = onCancel || null;
    setConfirmDialog({ open: true, title, message, onConfirm, danger });
  };
  const closeConfirmDialog = (confirmed) => {
    const onCancel = confirmCancelRef.current;
    confirmCancelRef.current = null;
    if (confirmed !== true) onCancel?.();
    setConfirmDialog(d => ({ ...d, open: false, onConfirm: null }));
  };

  // Abort controllers
  const waTemplatesAbortRef = useRef(null);
  const smsTemplatesAbortRef = useRef(null);

  // ── Permission check ──────────────────────────────────────────
  const canAccess = useCallback((permKeys) => {
    if (!user) return true;
    return hasAnyPermission(user, Array.isArray(permKeys) ? permKeys : [permKeys]);
  }, [user]);

  // ── Shared API functions ──────────────────────────────────────
  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/notifications/twilio/templates`);
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/notifications/twilio/settings`);
      setSettings(res.data);
    } catch { /* ignore */ }
  };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/notifications/twilio/status`);
      setTwilioStatus(res.data);
    } catch { /* ignore */ }
  };

  const fetchBatches = async () => {
    try {
      const selectedBranchId = getSelectedBranchId() || '';
      const res = await axios.get(`${API}/batches`, { params: selectedBranchId ? { branch_id: selectedBranchId } : {} });
      setBatches(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ }
  };

  const fetchWaTemplates = useCallback(async (signal) => {
    try {
      const [providerRes, localRes] = await Promise.allSettled([
        axios.get(`${API}/notifications/templates`, { params: { channel: 'whatsapp' }, signal }),
        axios.get(`${API}/notifications/local-templates`, { params: { channel: 'whatsapp' }, signal }),
      ]);
      if (signal?.aborted) return;
      const isRealFailure = (r) => r.status === 'rejected' && !axios.isCancel(r.reason);
      const bothFailed = isRealFailure(providerRes) && isRealFailure(localRes);
      if (bothFailed) {
        setWaTemplateFetchError(true);
        return;
      }
      const rawProviderTemplates = providerRes.status === 'fulfilled' && Array.isArray(providerRes.value.data)
        ? providerRes.value.data
        : [];
      const providerTemplates = rawProviderTemplates.map(t => {
        const bodyComponent = Array.isArray(t.components)
          ? t.components.find(c => c.type === 'BODY')
          : null;
        return { ...t, _body: bodyComponent?.text || '' };
      });
      const localRaw = localRes.status === 'fulfilled'
        ? (localRes.value.data?.templates || [])
        : [];
      const localTpls = localRaw.map(t => {
        const vars = Array.isArray(t.variables) ? t.variables : [];
        const variable_map = t.variable_map && Object.keys(t.variable_map).length > 0
          ? t.variable_map
          : vars.reduce((acc, v, i) => ({ ...acc, [String(i + 1)]: v }), {});
        return {
          template_name: t.provider_template_name || t.name,
          name: `${t.name} (Local)`,
          _isLocal: true,
          _localName: t.name,
          _body: t.body || '',
          _providerTemplateName: t.provider_template_name || null,
          variable_map,
        };
      });
      setWaTemplates([...providerTemplates, ...localTpls]);
      setWaTemplateFetchError(false);
    } catch (e) {
      if (!axios.isCancel(e)) setWaTemplateFetchError(true);
    }
  }, []);

  const fetchSmsTemplates = useCallback(async (signal) => {
    try {
      const res = await axios.get(`${API}/notifications/local-templates`, { params: { channel: 'sms' }, signal });
      const raw = res.data?.templates || [];
      setSmsTemplates(raw.map(t => ({
        _localName: t.name,
        name: `${t.name} (Local)`,
        flow_id: t.provider_template_name || t.name,
        _body: t.body || '',
      })));
      setSmsTemplateFetchError(false);
    } catch (e) {
      if (!axios.isCancel(e)) setSmsTemplateFetchError(true);
    }
  }, []);

  const fetchNotifRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/twilio/notification-rules`);
      setNotifRules(res.data || {});
    } catch { /* ignore */ }
  }, []);

  const fetchNotifEventRegistry = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/notifications/notification-rules/registry`);
      const events = r.data?.events || [];
      if (events.length > 0) setNotifEventRegistry(events);
    } catch { /* ignore */ }
  }, []);

  const fetchLocalTemplates = useCallback(async () => {
    setLocalTemplatesLoading(true);
    try {
      const res = await axios.get(`${API}/notifications/local-templates`, { params: { include_inactive: true } });
      const tplData = res.data?.templates ?? res.data;
      setLocalTemplates(Array.isArray(tplData) ? tplData : []);
    } catch { /* ignore */ }
    setLocalTemplatesLoading(false);
  }, []);

  const refreshWaAndSmsTemplates = useCallback(() => {
    waTemplatesAbortRef.current?.abort();
    smsTemplatesAbortRef.current?.abort();
    const waCtrl = new AbortController();
    const smsCtrl = new AbortController();
    waTemplatesAbortRef.current = waCtrl;
    smsTemplatesAbortRef.current = smsCtrl;
    fetchWaTemplates(waCtrl.signal);
    fetchSmsTemplates(smsCtrl.signal);
  }, [fetchWaTemplates, fetchSmsTemplates]);

  const saveNotificationSettings = async (patch) => {
    if (settingsSaving) return;
    const previous = settings;
    const updated = { ...settings, ...patch };
    setSettings(updated);
    setSettingsSaving(true);
    try {
      const res = await axios.put(`${API}/notifications/twilio/settings`, updated);
      setSettings(res.data?.settings || updated);
      toast.success('Settings updated');
    } catch {
      setSettings(previous);
      toast.error('Failed to update settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleSettingsUpdate = (key, value) => saveNotificationSettings({ [key]: value });

  // ── Initial fetch ─────────────────────────────────────────────
  useEffect(() => {
    fetchTemplates();
    fetchSettings();
    fetchStatus();
    fetchBatches();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const syncBranchScopedData = () => fetchBatches();
    window.addEventListener('storage', syncBranchScopedData);
    window.addEventListener('selected-branch-changed', syncBranchScopedData);
    return () => {
      window.removeEventListener('storage', syncBranchScopedData);
      window.removeEventListener('selected-branch-changed', syncBranchScopedData);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Context value ─────────────────────────────────────────────
  const value = {
    user,
    API,
    canAccess,
    // Shared data
    templates, setTemplates,
    settings, setSettings, settingsSaving,
    batches, setBatches,
    twilioStatus, setTwilioStatus,
    waTemplates, setWaTemplates,
    smsTemplates, setSmsTemplates,
    waTemplateFetchError, setWaTemplateFetchError,
    smsTemplateFetchError, setSmsTemplateFetchError,
    notifRules, setNotifRules,
    notifEventRegistry, setNotifEventRegistry,
    localTemplates, setLocalTemplates,
    localTemplatesLoading,
    // Shared functions
    fetchTemplates,
    fetchSettings,
    fetchStatus,
    fetchBatches,
    fetchWaTemplates,
    fetchSmsTemplates,
    fetchNotifRules,
    fetchNotifEventRegistry,
    fetchLocalTemplates,
    refreshWaAndSmsTemplates,
    saveNotificationSettings,
    handleSettingsUpdate,
    // Abort refs
    waTemplatesAbortRef,
    smsTemplatesAbortRef,
    // Confirm dialog
    confirmDialog, openConfirmDialog, closeConfirmDialog,
  };

  return (
    <CommunicationContext.Provider value={value}>
      {children}
    </CommunicationContext.Provider>
  );
}
